'use client';

import { Button, Card, Input } from '@clickt/brand';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Mail } from 'lucide-react';
import { useState } from 'react';
import { ThemedLogo } from '../components/ThemedLogo.tsx';
import { authClient } from '../lib/auth-client.ts';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { error } = await authClient.requestPasswordReset({
        email: vars.email,
        redirectTo: '/reset-password',
      });
      // Better-Auth returns 200 even for unknown emails, so we always show the
      // success state. Any real error (network, malformed request) still surfaces.
      if (error) throw error;
    },
    onSuccess: () => setSubmitted(true),
    onError: () => setSubmitted(true),
  });

  return (
    <div className="min-h-screen flex flex-col surface-dotted">
      <header className="flex items-center px-6 lg:px-10 py-6">
        <a
          href="https://clickt.link"
          className="flex items-center rounded-sm focus-visible:outline-none focus-visible:shadow-focus-ring"
          aria-label="clickt home"
        >
          <ThemedLogo className="h-8 w-auto" />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md space-y-6">
          {submitted ? (
            <Card padding="lg">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="grid h-12 w-12 place-items-center rounded-pill bg-electric/10">
                  <Mail className="h-6 w-6 text-electric" aria-hidden />
                </div>
                <h1 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
                  Check your email.
                </h1>
                <p className="text-body-md text-cool-gray leading-relaxed">
                  If an account exists for <span className="font-medium">{email}</span>, a reset
                  link is on its way.
                </p>
                <Link
                  to="/login"
                  className="text-[14px] text-midnight font-medium hover:text-electric transition-colors duration-fast"
                >
                  Back to sign in
                </Link>
              </div>
            </Card>
          ) : (
            <>
              <div className="space-y-3 text-center">
                <h1 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
                  Reset your password.
                </h1>
                <p className="text-body-md text-cool-gray">
                  Enter your email. We'll send a reset link.
                </p>
              </div>
              <Card padding="lg">
                <form
                  className="space-y-5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    mutation.mutate({ email });
                  }}
                >
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                    placeholder="you@yourdomain.com"
                  />
                  <Button type="submit" size="lg" className="w-full" disabled={mutation.isPending}>
                    {mutation.isPending ? 'Sending…' : 'Send reset link'}
                    {!mutation.isPending && <ArrowRight className="h-4 w-4" />}
                  </Button>
                </form>
              </Card>
              <p className="text-center text-[14px] text-cool-gray">
                Remembered it?{' '}
                <Link
                  to="/login"
                  className="text-midnight font-medium hover:text-electric transition-colors duration-fast"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
